// React imports
import { useState, useMemo, useEffect } from 'react';

// Generic imports
import _ from 'lodash';
import axios from 'axios';

// MUI imports
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import { grey } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme } from '@mui/material/styles';
import { darken, lighten } from '@mui/system';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash, faCircleExclamation } from '@fortawesome/pro-light-svg-icons';
import { faUpload, faPlaneDeparture } from '@fortawesome/free-solid-svg-icons';
import { addClassNumber } from '../../utils/Dangerous.js';

// Local imports
import Product from '../../Product';
import PRODUCT_TYPES from '../../ProductTypes.js';
import { ProductDescription } from '../../utils/ProductHelperComponents.js';
import { createEmptyProduct } from '../utils/product.js';
import { v4 as uuidv4 } from 'uuid';

function OpportunityProductsPane(props) {
    const { products, setProducts, errors = [], setErrors = () => {}, transportationMethod, organization, accesstoken } = props;
    const [ selection, setSelection ] = useState([0]);
    const theme = useTheme();

    const isSea = transportationMethod === 'sea';
    const isAir = transportationMethod === 'air';
    const lclProduct = isSea ? products.find(p => p.type === 'LCL') : null;
    const lclProductIndex = isSea ? products.findIndex(p => p.type === 'LCL') : -1;
    const containerProducts = isSea ? products.filter(p => p.type !== 'LCL') : products;
    const hasLCL = !!lclProduct;
    const airFreightProduct = isAir ? products.find(p => p.type === 'Air freight') : null;
    const airFreightProductIndex = isAir ? products.findIndex(p => p.type === 'Air freight') : -1;

    // Sea tab: 0 = FCL, 1 = LCL
    const [seaTab, setSeaTab] = useState(hasLCL && containerProducts.length === 0 ? 1 : 0);
    // Active container sub-tab index (within containerProducts)
    const [activeContainerTab, setActiveContainerTab] = useState(0);

    // When switching to LCL tab, ensure an LCL product exists
    // When switching away from LCL tab, remove empty LCL (no calculations)
    function handleSeaTabChange(newTab) {
        if (newTab === 1 && !lclProduct) {
            setProducts([...products, createEmptyProduct({ type: 'LCL' })]);
        }
        if (newTab === 0 && lclProduct && (!lclProduct.calculations || lclProduct.calculations.length === 0)) {
            setProducts(products.filter(p => p.id !== lclProduct.id));
        }
        setSeaTab(newTab);
    }

    // Auto-create Air freight product when air transport is selected
    useEffect(() => {
        if (isAir && !airFreightProduct) {
            setProducts([...products, createEmptyProduct({ type: 'Air freight' })]);
        }
    }, [isAir]);

    // Auto-expand accordions that have errors (non-sea only)
    useEffect(() => {
        if (!isSea && !isAir && !_.isEmpty(errors)) {
            const productIndicesWithErrors = errors
                .filter(e => e.startsWith('product:'))
                .map(e => parseInt(e.split(':')[1]))
                .filter(i => !isNaN(i));
            const uniqueIndices = _.uniq(productIndicesWithErrors);
            if (!_.isEmpty(uniqueIndices)) {
                setSelection(s => _.uniq([...s, ...uniqueIndices]));
            }
        }
    }, [errors]);

    // For sea, render the product list based on the active tab
    const displayProducts = isSea ? (seaTab === 0 ? containerProducts : (lclProduct ? [lclProduct] : [])) : products;

    // Map display index back to products array index
    const getProductIndex = (displayIndex) => {
        if (!isSea) return displayIndex;
        const product = displayProducts[displayIndex];
        return products.findIndex(p => p.id === product.id);
    };

    // Compute which products have errors
    const productErrorIndices = useMemo(() => {
        return errors
            .filter(e => e.startsWith('product:'))
            .map(e => parseInt(e.split(':')[1]))
            .filter(i => !isNaN(i));
    }, [errors]);

    const fclHasErrors = isSea && containerProducts.some(p => productErrorIndices.includes(products.indexOf(p)));
    const lclHasErrors = isSea && lclProductIndex >= 0 && productErrorIndices.includes(lclProductIndex);

    // Per-container error check
    const containerHasError = (ci) => {
        const product = containerProducts[ci];
        if (!product) return false;
        const globalIndex = products.indexOf(product);
        return productErrorIndices.includes(globalIndex);
    };

    // --- Shared header: Weight Unit / Dimension Unit (cross-product) ---
    const productNeedsKg = (p) => {
        if (!p?.type) return false;
        const entry = (PRODUCT_TYPES[transportationMethod] || []).find(x => x.name === p.type);
        const ask = entry?.ask || [];
        return ask.includes('weight') || ask.includes('chargeable-weight') || ask.includes('weight-or-measurement') || ask.includes('lcl-calculations') || ask.includes('air-calculations');
    };
    const productNeedsCm = (p) => {
        if (!p?.type) return false;
        const entry = (PRODUCT_TYPES[transportationMethod] || []).find(x => x.name === p.type);
        const ask = entry?.ask || [];
        return ask.includes('dimensions') || ask.includes('height') || ask.includes('lcl-calculations') || ask.includes('air-calculations');
    };
    const productNeedsTemp = (p) => {
        if (!p?.type) return false;
        const entry = (PRODUCT_TYPES[transportationMethod] || []).find(x => x.name === p.type);
        const ask = entry?.ask || [];
        return ask.includes('temperature');
    };
    const anyNeedsKg = products.some(productNeedsKg);
    const anyNeedsCm = products.some(productNeedsCm);
    const anyNeedsTemp = products.some(productNeedsTemp);
    // Resolve current unit — use the first product that has it set, else default
    const sharedKgOrLbs = (products.find(p => productNeedsKg(p) && p.kgOrLbs)?.kgOrLbs) || 'kg';
    const sharedCmOrInches = (products.find(p => productNeedsCm(p) && p.cmOrInches)?.cmOrInches) || 'cm';
    const sharedCelsiusOrFahrenheit = (products.find(p => productNeedsTemp(p) && p.celsiusOrFahrenheit)?.celsiusOrFahrenheit) || 'c';
    function setSharedKgOrLbs(v) {
        setProducts(products.map(p => productNeedsKg(p) ? { ...p, kgOrLbs: v } : p));
    }
    function setSharedCmOrInches(v) {
        setProducts(products.map(p => productNeedsCm(p) ? { ...p, cmOrInches: v } : p));
    }
    function setSharedCelsiusOrFahrenheit(v) {
        setProducts(products.map(p => productNeedsTemp(p) ? { ...p, celsiusOrFahrenheit: v } : p));
    }

    // --- LCL shipment-level toggles (wired to the LCL product) ---
    function setLclField(name, value) {
        if (!lclProduct) return;
        setProducts(products.map(p => p.id === lclProduct.id ? { ...p, [name]: value } : p));
    }
    // --- Air freight shipment-level toggles (wired to the Air freight product) ---
    function setAirField(name, value) {
        if (!airFreightProduct) return;
        setProducts(products.map(p => p.id === airFreightProduct.id ? { ...p, [name]: value } : p));
    }

    const showSharedUnits = anyNeedsKg || anyNeedsCm || isSea || isAir;
    const showShipmentBarLcl = isSea && seaTab === 1 && !!lclProduct;
    const showShipmentBarAir = isAir && !!airFreightProduct;
    const showShipmentBar = showShipmentBarLcl || showShipmentBarAir;
    const activeShipmentProduct = showShipmentBarLcl ? lclProduct : (showShipmentBarAir ? airFreightProduct : null);
    const setShipmentField = showShipmentBarLcl ? setLclField : (showShipmentBarAir ? setAirField : () => {});
    const showDangerousBar = showShipmentBar && !!activeShipmentProduct?.dangerous;

    const [uploadingMsds, setUploadingMsds] = useState(false);
    const [ msdsError, setMsdsError ] = useState(null);
    // The upload stores a link whose last segment is a generated id followed by the name the file
    // was given. Showing that name is the point of the button: "View MSDS" tells someone a file is
    // attached, the filename tells them which one, which is what matters when they are chasing the
    // right document for a dangerous shipment.
    function msdsFileName(url) {
        try {
            const base = decodeURIComponent(String(url || '').split('/').pop().split('?')[0]);
            return base.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\./i, '') || base;
        } catch (e) {
            return null;
        }
    }

    async function onUploadMsds(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file, file.name);
        setUploadingMsds(true);
        setMsdsError(null);
        try {
            const { data } = await axios({
                url: `/api/opportunities/upload_file?accesstoken=${accesstoken}&organizationId=${organization.id}`,
                method: 'POST',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            // The endpoint answers with `location`. Reading `url` off it stored undefined, so the
            // file reached S3, the button fell back to "Upload MSDS" as though nothing had
            // happened, and the link was gone by the time anyone looked for it.
            if (!data?.location) throw new Error('The upload did not come back with a link to the file.');
            setShipmentField('dangerousMSDS', data.location);
        } catch (err) {
            setMsdsError(err?.response?.data?.detail || err?.message || 'The file could not be uploaded.');
        } finally {
            setUploadingMsds(false);
        }
    }

    const UnitSelectors = showSharedUnits ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '13px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mr: 0.5 }}>
                Units:
            </Typography>
            <Box
                onClick={() => anyNeedsKg && setSharedKgOrLbs(sharedKgOrLbs === 'kg' ? 'lbs' : 'kg')}
                sx={{ px: 1, py: 0.5, borderRadius: '4px', cursor: anyNeedsKg ? 'pointer' : 'default', color: anyNeedsKg ? 'primary.main' : 'text.disabled', fontWeight: 600, '&:hover': anyNeedsKg ? { backgroundColor: 'rgba(25, 118, 210, 0.08)' } : {} }}
            >
                {sharedKgOrLbs === 'kg' ? 'KG' : 'LBS'}
            </Box>
            <Typography sx={{ color: 'text.disabled' }}>/</Typography>
            <Box
                onClick={() => anyNeedsCm && setSharedCmOrInches(sharedCmOrInches === 'cm' ? 'in.' : 'cm')}
                sx={{ px: 1, py: 0.5, borderRadius: '4px', cursor: anyNeedsCm ? 'pointer' : 'default', color: anyNeedsCm ? 'primary.main' : 'text.disabled', fontWeight: 600, '&:hover': anyNeedsCm ? { backgroundColor: 'rgba(25, 118, 210, 0.08)' } : {} }}
            >
                {sharedCmOrInches === 'cm' ? 'CM' : 'IN'}
            </Box>
            <Typography sx={{ color: 'text.disabled' }}>/</Typography>
            <Box
                onClick={() => anyNeedsTemp && setSharedCelsiusOrFahrenheit(sharedCelsiusOrFahrenheit === 'c' ? 'f' : 'c')}
                sx={{ px: 1, py: 0.5, borderRadius: '4px', cursor: anyNeedsTemp ? 'pointer' : 'default', color: anyNeedsTemp ? 'primary.main' : 'text.disabled', fontWeight: 600, '&:hover': anyNeedsTemp ? { backgroundColor: 'rgba(25, 118, 210, 0.08)' } : {} }}
            >
                {sharedCelsiusOrFahrenheit === 'c' ? '°C' : '°F'}
            </Box>
        </Box>
    ) : null;

    return (
        <Box>
            {!isSea && !isAir && showSharedUnits && (
                <Box sx={{ mb: 2 }}>{UnitSelectors}</Box>
            )}
            {isAir && (
                <Box sx={{ display: 'flex', gap: 2, mb: 2, overflow: 'visible', pt: 1, pr: 1, alignItems: 'center' }}>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100px',
                            height: '90px',
                            borderRadius: '10px',
                            border: '2px solid',
                            borderColor: 'primary.main',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            color: 'primary.main',
                        }}
                    >
                        {(airFreightProduct?.calculations?.length || 0) > 0 && (
                            <Box sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                backgroundColor: '#4caf50',
                                color: '#fff',
                                borderRadius: '10px',
                                minWidth: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                fontWeight: 700,
                                px: '5px',
                            }}>
                                {airFreightProduct.calculations.length}
                            </Box>
                        )}
                        <FontAwesomeIcon icon={faPlaneDeparture} style={{ fontSize: '28px' }} />
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, mt: 0.5, lineHeight: 1 }}>AIR</Typography>
                        <Typography sx={{ fontSize: '9px', opacity: 0.6, lineHeight: 1, mt: 0.3 }}>Air Freight</Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                        {airFreightProduct && (
                            <FormControlLabel
                                control={<Switch size='small' checked={airFreightProduct.roundChargeableWeight !== false} onChange={(e) => setAirField('roundChargeableWeight', e.target.checked)} />}
                                label={<Typography sx={{ fontSize: '13px' }}>Round Chargeable Weight</Typography>}
                            />
                        )}
                        {UnitSelectors}
                    </Box>
                </Box>
            )}
            {isSea && (
                <Box sx={{ display: 'flex', gap: 2, mb: 2, overflow: 'visible', pt: 1, pr: 1, alignItems: 'center' }}>
                    {[
                        {
                            label: 'FCL',
                            subtitle: 'Full Container',
                            value: 0,
                            count: containerProducts.length,
                            hasError: fclHasErrors,
                            icon: (
                                <svg width="56" height="32" viewBox="0 0 56 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    {/* Side panel */}
                                    <polygon points="18,4 54,2 54,28 18,30" stroke="currentColor" strokeWidth="1.1" fill="currentColor" fillOpacity="0.03"/>
                                    {/* Side corrugation */}
                                    {[21,24,27,30,33,36,39,42,45,48,51].map(x => (
                                        <line key={x} x1={x} y1={4 - (x-18)*2/36 + 1.5} x2={x} y2={30 - (x-18)*2/36} stroke="currentColor" strokeWidth="0.4" opacity="0.25"/>
                                    ))}
                                    {/* Top panel */}
                                    <polygon points="2,6 18,4 54,2 38,4" stroke="currentColor" strokeWidth="0.9" fill="currentColor" fillOpacity="0.06"/>
                                    {/* Front face */}
                                    <polygon points="2,6 18,4 18,30 2,31" stroke="currentColor" strokeWidth="1.1" fill="currentColor" fillOpacity="0.05"/>
                                    {/* Door split */}
                                    <line x1="10" y1="5" x2="10" y2="30.5" stroke="currentColor" strokeWidth="0.7"/>
                                    {/* Left door bar + handles */}
                                    <line x1="5.5" y1="7" x2="5.5" y2="30.5" stroke="currentColor" strokeWidth="0.6"/>
                                    <circle cx="5.5" cy="15" r="0.7" fill="currentColor"/>
                                    <circle cx="5.5" cy="22" r="0.7" fill="currentColor"/>
                                    {/* Right door bar + handles */}
                                    <line x1="14.5" y1="5.5" x2="14.5" y2="30" stroke="currentColor" strokeWidth="0.6"/>
                                    <circle cx="14.5" cy="14" r="0.7" fill="currentColor"/>
                                    <circle cx="14.5" cy="21" r="0.7" fill="currentColor"/>
                                    {/* Corner posts */}
                                    <line x1="2" y1="6" x2="2" y2="31" stroke="currentColor" strokeWidth="1.8"/>
                                    <line x1="18" y1="4" x2="18" y2="30" stroke="currentColor" strokeWidth="1.8"/>
                                    <line x1="54" y1="2" x2="54" y2="28" stroke="currentColor" strokeWidth="1.3"/>
                                    {/* Bottom rail */}
                                    <line x1="2" y1="31" x2="18" y2="30" stroke="currentColor" strokeWidth="1.3"/>
                                    <line x1="18" y1="30" x2="54" y2="28" stroke="currentColor" strokeWidth="1"/>
                                </svg>
                            ),
                        },
                        {
                            label: 'LCL',
                            subtitle: 'Loose Cargo',
                            value: 1,
                            count: lclProduct?.calculations?.length || 0,
                            hasError: lclHasErrors,
                            icon: (
                                <svg width="48" height="36" viewBox="0 0 48 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    {/* Large box (bottom) */}
                                    <rect x="2" y="18" width="44" height="16" rx="1.2" stroke="currentColor" strokeWidth="1.1"/>
                                    <line x1="2" y1="22" x2="46" y2="22" stroke="currentColor" strokeWidth="0.8"/>
                                    <line x1="24" y1="18" x2="24" y2="22" stroke="currentColor" strokeWidth="0.8"/>
                                    <line x1="17" y1="22" x2="17" y2="34" stroke="currentColor" strokeWidth="0.4" opacity="0.25"/>
                                    <line x1="31" y1="22" x2="31" y2="34" stroke="currentColor" strokeWidth="0.4" opacity="0.25"/>
                                    {/* Medium box (top-left) */}
                                    <rect x="2" y="4" width="22" height="14" rx="1.2" stroke="currentColor" strokeWidth="1.1"/>
                                    <line x1="2" y1="8" x2="24" y2="8" stroke="currentColor" strokeWidth="0.8"/>
                                    <line x1="13" y1="4" x2="13" y2="8" stroke="currentColor" strokeWidth="0.8"/>
                                    <line x1="9" y1="8" x2="9" y2="18" stroke="currentColor" strokeWidth="0.4" opacity="0.25"/>
                                    <line x1="17" y1="8" x2="17" y2="18" stroke="currentColor" strokeWidth="0.4" opacity="0.25"/>
                                    {/* Small box (top-right) */}
                                    <rect x="26" y="6" width="20" height="12" rx="1.2" stroke="currentColor" strokeWidth="1.1"/>
                                    <line x1="26" y1="9.5" x2="46" y2="9.5" stroke="currentColor" strokeWidth="0.8"/>
                                    <line x1="36" y1="6" x2="36" y2="9.5" stroke="currentColor" strokeWidth="0.8"/>
                                    <line x1="32" y1="9.5" x2="32" y2="18" stroke="currentColor" strokeWidth="0.4" opacity="0.25"/>
                                    <line x1="40" y1="9.5" x2="40" y2="18" stroke="currentColor" strokeWidth="0.4" opacity="0.25"/>
                                </svg>
                            ),
                        },
                    ].map(tab => (
                        <Box
                            key={tab.value}
                            onClick={() => handleSeaTabChange(tab.value)}
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100px',
                                height: '90px',
                                borderRadius: '10px',
                                border: tab.hasError ? '2px solid' : seaTab === tab.value ? '2px solid' : '1px solid',
                                borderColor: tab.hasError ? '#d32f2f' : seaTab === tab.value ? 'primary.main' : '#e0e0e0',
                                backgroundColor: seaTab === tab.value ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                color: seaTab === tab.value ? 'primary.main' : 'text.secondary',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                },
                            }}
                        >
                            {tab.count > 0 && (
                                <Box sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    backgroundColor: '#4caf50',
                                    color: '#fff',
                                    borderRadius: '10px',
                                    minWidth: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    px: '5px',
                                }}>
                                    {tab.count}
                                </Box>
                            )}
                            {tab.icon}
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, mt: 0.5, lineHeight: 1 }}>{tab.label}</Typography>
                            <Typography sx={{ fontSize: '9px', opacity: 0.6, lineHeight: 1, mt: 0.3 }}>{tab.subtitle}</Typography>
                        </Box>
                    ))}
                    {UnitSelectors && <Box sx={{ ml: 'auto' }}>{UnitSelectors}</Box>}
                </Box>
            )}
            {showShipmentBar && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, px: 2, py: 1, backgroundColor: 'rgba(25, 118, 210, 0.04)', border: '1px solid', borderColor: 'primary.light', borderRadius: '6px' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', color: 'primary.main', textTransform: 'uppercase', mr: 1 }}>
                        Shipment
                    </Typography>
                    <FormControlLabel
                        control={<Switch checked={!activeShipmentProduct.nonstackable} onChange={() => setShipmentField('nonstackable', !activeShipmentProduct.nonstackable)} />}
                        label='Stackable'
                    />
                    <FormControlLabel
                        control={<Switch checked={!!activeShipmentProduct.dangerous} onChange={() => setShipmentField('dangerous', !activeShipmentProduct.dangerous)} />}
                        label='Dangerous'
                    />
                </Box>
            )}
            {showDangerousBar && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, px: 2, py: 1, backgroundColor: 'rgba(211, 47, 47, 0.04)', border: '1px solid', borderColor: '#f5b7b1', borderRadius: '6px' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', color: '#d32f2f', textTransform: 'uppercase', mr: 1, whiteSpace: 'nowrap' }}>
                        Dangerous
                    </Typography>
                    <FormControl size='small' sx={{ minWidth: 180 }}>
                        <InputLabel>Class</InputLabel>
                        <Select label='Class' value={activeShipmentProduct.dangerousClass || ''} onChange={(e) => setShipmentField('dangerousClass', e.target.value)}>
                            {['Explosives', 'Flammable Gases', 'Flammable Liquids', 'Flammable Solids', 'Oxidizing', 'Toxic & Infectious', 'Radioactive', 'Corrosives', 'Miscellaneous'].map(_class => (
                                <MenuItem key={_class} value={_class}>{addClassNumber({ dangerousClass: _class })}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size='small'
                        label='UN Code'
                        value={activeShipmentProduct.dangerousUNCode || ''}
                        onChange={(e) => setShipmentField('dangerousUNCode', e.target.value)}
                        sx={{ minWidth: 140 }}
                    />
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                        {activeShipmentProduct.dangerousMSDS ? (
                            <>
                                <Button size='small' variant='outlined' component='a' href={activeShipmentProduct.dangerousMSDS} target='_blank' rel='noopener noreferrer'
                                    sx={{ textTransform: 'none', maxWidth: '260px', '& .MuiButton-label, &': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' } }}
                                    title={msdsFileName(activeShipmentProduct.dangerousMSDS) || 'View MSDS'}
                                >
                                    {msdsFileName(activeShipmentProduct.dangerousMSDS) || 'View MSDS'}
                                </Button>
                                <Button size='small' variant='text' color='error' sx={{ textTransform: 'none' }} onClick={() => setShipmentField('dangerousMSDS', null)}>
                                    Remove
                                </Button>
                            </>
                        ) : msdsError && !uploadingMsds ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant='caption' sx={{ color: 'error.main' }}>{msdsError}</Typography>
                                <Button size='small' variant='contained' component='label' sx={{ textTransform: 'none' }}>
                                    <FontAwesomeIcon icon={faUpload} />&nbsp;Try again
                                    <input type='file' hidden onChange={onUploadMsds} />
                                </Button>
                            </Box>
                        ) : uploadingMsds ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={14} />
                                <Typography variant='caption'>Uploading...</Typography>
                            </Box>
                        ) : (
                            <Button size='small' variant='contained' component='label' sx={{ textTransform: 'none' }}>
                                <FontAwesomeIcon icon={faUpload} />&nbsp;Upload MSDS
                                <input type='file' hidden onChange={onUploadMsds} />
                            </Button>
                        )}
                    </Box>
                </Box>
            )}
            {showShipmentBar && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', color: 'text.secondary', textTransform: 'uppercase' }}>
                        Packages · {(activeShipmentProduct.calculations || []).length}
                    </Typography>
                    <Button
                        variant='outlined'
                        size='small'
                        sx={{ ml: 'auto', textTransform: 'none', borderStyle: 'dashed', fontWeight: 700 }}
                        onClick={() => {
                            const defaultMethod = showShipmentBarAir ? 'dimensions' : 'volume';
                            setShipmentField('calculations', [...(activeShipmentProduct.calculations || []), { id: uuidv4(), method: defaultMethod, quantity: 1, commodity: 'General' }]);
                        }}
                    >
                        + ADD PACKAGE
                    </Button>
                </Box>
            )}
            {/* Shared Product renderer */}
            {(() => {
                function renderProduct(product) {
                    const i = products.findIndex(p => p.id === product.id);
                    function returnSetter({propName, errorName}) {
                        return (v) => {
                            if (errorName) setErrors(_.difference(errors, errorName.map(e => `product:${i}:${e}`)));
                            if (propName === 'type') {
                                // Preserve the product id across a type change (as changeContainerType
                                // already does). Default costs / sell rates are keyed to the product id
                                // via field.buyingRates[].opportunityProductId and field.values, and are
                                // shown/hidden purely by the type guard in getFieldRatesForProduct. Minting
                                // a new id here orphaned them, so switching a type away and back never
                                // restored the template's default costs.
                                setProducts(_products => _products.map((p, i2) => i !== i2 ? p : { ...createEmptyProduct({ transportationMethod, type: v }), id: p.id, [propName]: v }));
                            } else {
                                setProducts(_products => _products.map((p, i2) => i !== i2 ? p : { ...p, [propName]: v }));
                            }
                        }
                    }
                    function setProduct(updatedProduct) {
                        setProducts(_products => _products.map((p, i2) => i !== i2 ? p : updatedProduct));
                    }
                    return (
                        <Product
                            key={product.id} transportationMethod={transportationMethod} PRODUCT_TYPES={PRODUCT_TYPES}
                            _key={product.id} product={product} setProduct={setProduct} allProducts={products}
                            type={product.type} cmOrInches={product.cmOrInches} setCmOrInches={returnSetter({ propName: 'cmOrInches' })}
                            width={product.width} setWidth={returnSetter({ propName: 'width', errorName: ['width'] })}
                            height={product.height} setHeight={returnSetter({ propName: 'height', errorName: ['height'] })}
                            length={product.length} setLength={returnSetter({ propName: 'length', errorName: ['length'] })}
                            setType={returnSetter({ propName: 'type', errorName: ['type'] })}
                            kgOrLbs={product.kgOrLbs} setKgOrLbs={returnSetter({ propName: 'kgOrLbs' })}
                            nonstackable={product.nonstackable} setNonStackable={returnSetter({ propName: 'nonstackable' })}
                            dangerous={product.dangerous} setDangerous={returnSetter({ propName: 'dangerous' })}
                            chargeableWeight={product.chargeableWeight} setChargeableWeight={returnSetter({ propName: 'chargeableWeight', errorName: ['chargeableWeight', 'air-freight-chargeable-weight-gross-weight'] })}
                            temperature={product.temperature} setTemperature={returnSetter({ propName: 'temperature', errorName: ['temperature'] })}
                            celsiusOrFahrenheit={product.celsiusOrFahrenheit} setCelsiusOrFahrenheit={returnSetter({ propName: 'celsiusOrFahrenheit' })}
                            dangerousClass={product.dangerousClass} setDangerousClass={returnSetter({ propName: 'dangerousClass', errorName: ['dangerousClass'] })}
                            dangerousUNCode={product.dangerousUNCode} setDangerousUNCode={returnSetter({ propName: 'dangerousUNCode', errorName: ['dangerousUNCode'] })}
                            dangerousMSDS={product.dangerousMSDS} setDangerousMSDS={returnSetter({ propName: 'dangerousMSDS', errorName: ['dangerousMSDS'] })}
                            quantity={product.quantity} setQuantity={returnSetter({ propName: 'quantity', errorName: ['quantity'] })}
                            weight={product.weight} setWeight={returnSetter({ propName: 'weight', errorName: ['weight', 'air-freight-chargeable-weight-gross-weight'] })}
                            volume={product.volume} setVolume={returnSetter({ propName: 'volume', errorName: ['volume'] })}
                            commodity={product.commodity} setCommodity={returnSetter({ propName: 'commodity' })}
                            errors={errors.filter(e => e.startsWith(`product:${i}`)).map(e => e.split(':')[2])}
                            organization={organization} accesstoken={accesstoken}
                            hideUnitSelectors={showSharedUnits}
                            // Only when the shared air bar is actually drawing one. It is drawn for
                            // an air quote, and air cargo now rides land quotes too — where that bar
                            // never appears, so hiding the product's own toggle alongside the unit
                            // selectors lost it altogether: the units moved to the shared header and
                            // the toggle moved nowhere.
                            hideRoundChargeableWeight={showShipmentBarAir}
                            hideShipmentToggles={(isSea || isAir) && (product.type === 'LCL' || product.type === 'Air freight')}
                            hideInlineUnitToggles={(isSea || isAir) && showSharedUnits}
                            hideAddPackageButton={(isSea || isAir) && (product.type === 'LCL' || product.type === 'Air freight')}
                            hideDangerousDetails={(isSea || isAir) && (product.type === 'LCL' || product.type === 'Air freight')}
                        />
                    );
                }

                /* Sea FCL tab: container cards list */
                if (isSea && seaTab === 0) {
                    const containerTypeOptions = (PRODUCT_TYPES.sea || []).filter(t => !t.name.startsWith('By') && t.name !== 'LCL');
                    function updateContainer(productId, field, value) {
                        setErrors(errors.filter(e => !e.endsWith(`:${field}`)));
                        setProducts(products.map(p => p.id !== productId ? p : { ...p, [field]: value }));
                    }
                    function changeContainerType(productId, newType) {
                        setErrors([]);
                        setProducts(products.map(p => p.id !== productId ? p : { ...createEmptyProduct({ transportationMethod, type: newType }), id: p.id, type: newType, kgOrLbs: p.kgOrLbs, cmOrInches: p.cmOrInches, quantity: p.quantity, weight: p.weight, commodity: p.commodity, dangerous: p.dangerous }));
                    }
                    function deleteContainer(productId) {
                        setErrors([]);
                        setProducts(products.filter(p => p.id !== productId));
                    }
                    return (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', color: 'text.secondary', textTransform: 'uppercase' }}>
                                    Containers · {containerProducts.length}
                                </Typography>
                                <Button
                                    variant='outlined'
                                    size='small'
                                    sx={{ ml: 'auto', textTransform: 'none', borderStyle: 'dashed', fontWeight: 700 }}
                                    onClick={() => setProducts([...products, createEmptyProduct({ transportationMethod })])}
                                >
                                    + ADD CONTAINER
                                </Button>
                            </Box>
                            {containerProducts.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                                    <Typography variant='body2'>No containers added yet</Typography>
                                </Box>
                            )}
                            {containerProducts.map((c, ci) => {
                                const globalIndex = products.indexOf(c);
                                const hasError = productErrorIndices.includes(globalIndex);
                                const typeEntry = containerTypeOptions.find(t => t.name === c.type);
                                const ask = typeEntry?.ask || [];
                                const wUnit = c.kgOrLbs || sharedKgOrLbs || 'kg';
                                const dUnit = c.cmOrInches || sharedCmOrInches || 'cm';
                                const blueAdorn = (u) => ({ endAdornment: <InputAdornment position='end'><span style={{ color: '#1976d2', fontWeight: 600 }}>{u}</span></InputAdornment> });
                                return (
                                    <Box key={c.id} sx={{ border: '1px solid', borderColor: hasError ? '#d32f2f' : '#e2e8f0', borderRadius: '8px', p: '12px 16px', mb: '10px', backgroundColor: '#f8fafc' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: '14px', whiteSpace: 'nowrap' }}>
                                                Container #{ci + 1}
                                            </Typography>
                                            <FormControl size='small' sx={{ minWidth: 200 }} error={hasError && !c.type}>
                                                <Select value={c.type || ''} displayEmpty onChange={(e) => changeContainerType(c.id, e.target.value)}>
                                                    {containerTypeOptions.map(t => <MenuItem key={t.name} value={t.name}>{t.name}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                            <IconButton size='small' onClick={() => deleteContainer(c.id)} sx={{ ml: 'auto', color: '#94a3b8' }}>
                                                <FontAwesomeIcon icon={faTrash} style={{ fontSize: '14px' }} />
                                            </IconButton>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                            <TextField size='small' label='Qty' value={c.quantity || ''} onChange={(e) => updateContainer(c.id, 'quantity', e.target.value)} sx={{ flex: '0 0 90px' }} />
                                            {ask.includes('dimensions') && (
                                                <>
                                                    <TextField size='small' label='Length' value={c.length || ''} onChange={(e) => updateContainer(c.id, 'length', e.target.value)} InputProps={blueAdorn(dUnit)} sx={{ flex: 1, minWidth: 110 }} />
                                                    <TextField size='small' label='Width' value={c.width || ''} onChange={(e) => updateContainer(c.id, 'width', e.target.value)} InputProps={blueAdorn(dUnit)} sx={{ flex: 1, minWidth: 110 }} />
                                                    <TextField size='small' label='Height' value={c.height || ''} onChange={(e) => updateContainer(c.id, 'height', e.target.value)} InputProps={blueAdorn(dUnit)} sx={{ flex: 1, minWidth: 110 }} />
                                                </>
                                            )}
                                            {!ask.includes('dimensions') && ask.includes('height') && (
                                                <TextField size='small' label='Height' value={c.height || ''} onChange={(e) => updateContainer(c.id, 'height', e.target.value)} InputProps={blueAdorn(dUnit)} sx={{ flex: 1, minWidth: 110 }} />
                                            )}
                                            {ask.includes('temperature') && (
                                                <TextField size='small' label='Temp' value={c.temperature ?? ''} onChange={(e) => updateContainer(c.id, 'temperature', e.target.value)} InputProps={blueAdorn(c.celsiusOrFahrenheit === 'f' ? '°F' : '°C')} sx={{ flex: 1, minWidth: 110 }} />
                                            )}
                                            <TextField size='small' label='Weight' value={c.weight || ''} onChange={(e) => updateContainer(c.id, 'weight', e.target.value)} InputProps={blueAdorn(wUnit)} sx={{ flex: 1, minWidth: 110 }} />
                                            <TextField size='small' label='Commodity' value={c.commodity || ''} onChange={(e) => updateContainer(c.id, 'commodity', e.target.value)} sx={{ flex: 1.2, minWidth: 140 }} />
                                        </Box>
                                        <Box sx={{ mt: 1 }}>
                                            <FormControlLabel
                                                control={<Switch size='small' checked={!!c.dangerous} onChange={(e) => updateContainer(c.id, 'dangerous', e.target.checked)} />}
                                                label={<Typography sx={{ fontSize: '13px' }}>Dangerous</Typography>}
                                            />
                                        </Box>
                                        {c.dangerous && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, px: 2, py: 1, backgroundColor: 'rgba(211, 47, 47, 0.04)', border: '1px solid', borderColor: '#f5b7b1', borderRadius: '6px' }}>
                                                <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', color: '#d32f2f', textTransform: 'uppercase', mr: 1, whiteSpace: 'nowrap' }}>
                                                    Dangerous
                                                </Typography>
                                                <FormControl size='small' sx={{ minWidth: 180 }}>
                                                    <InputLabel>Class</InputLabel>
                                                    <Select label='Class' value={c.dangerousClass || ''} onChange={(e) => updateContainer(c.id, 'dangerousClass', e.target.value)}>
                                                        {['Explosives', 'Flammable Gases', 'Flammable Liquids', 'Flammable Solids', 'Oxidizing', 'Toxic & Infectious', 'Radioactive', 'Corrosives', 'Miscellaneous'].map(_class => (
                                                            <MenuItem key={_class} value={_class}>{addClassNumber({ dangerousClass: _class })}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <TextField
                                                    size='small'
                                                    label='UN Code'
                                                    value={c.dangerousUNCode || ''}
                                                    onChange={(e) => updateContainer(c.id, 'dangerousUNCode', e.target.value)}
                                                    sx={{ minWidth: 140 }}
                                                />
                                                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {c.dangerousMSDS ? (
                                                        <>
                                                            <Button size='small' variant='outlined' component='a' href={c.dangerousMSDS} target='_blank' rel='noopener noreferrer' title={msdsFileName(c.dangerousMSDS) || 'View MSDS'} sx={{ textTransform: 'none', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{msdsFileName(c.dangerousMSDS) || 'View MSDS'}</Button>
                                                            <Button size='small' variant='text' color='error' sx={{ textTransform: 'none' }} onClick={() => updateContainer(c.id, 'dangerousMSDS', null)}>Remove</Button>
                                                        </>
                                                    ) : (
                                                        <Button size='small' variant='contained' component='label' sx={{ textTransform: 'none' }}>
                                                            <FontAwesomeIcon icon={faUpload} />&nbsp;Upload MSDS
                                                            <input type='file' hidden onChange={async (e) => {
                                                                const file = e.target.files?.[0];
                                                                if (!file) return;
                                                                const formData = new FormData();
                                                                formData.append('file', file, file.name);
                                                                const { data } = await axios({
                                                                    url: `/api/opportunities/upload_file?accesstoken=${accesstoken}&organizationId=${organization.id}`,
                                                                    method: 'POST',
                                                                    data: formData,
                                                                    headers: { 'Content-Type': 'multipart/form-data' }
                                                                });
                                                                updateContainer(c.id, 'dangerousMSDS', data.location);
                                                            }} />
                                                        </Button>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    );
                }

                /* Sea LCL tab */
                if (isSea && seaTab === 1) {
                    // lclProduct is guaranteed to exist (created by handleSeaTabChange)
                    if (lclProduct) return renderProduct(lclProduct);
                    return null;
                }

                /* Air: single Air freight product, no accordion */
                if (isAir && airFreightProduct) {
                    return renderProduct(airFreightProduct);
                }

                /* Non-sea, non-air: accordion layout */
                return products.map((product, i) => (
                    <Accordion key={product.id} expanded={selection.includes(i)}
                        onChange={() => {
                            if (_.some(errors, e => e.startsWith(`product:${i}`)) && selection.includes(i)) return;
                            setSelection(s => _.xor(s, [i]));
                        }}
                        sx={{ marginBottom: '12px', background: theme.palette.mode === 'dark' ? darken(theme.palette.text.primary, 0.85) : lighten(theme.palette.text.primary, 0.98) }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, width: '100%' }}>
                                <Box style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <div style={{ fontWeight: 800 }}>Product #{i + 1}</div>
                                    <Typography variant='caption' component={Box}>
                                        <ProductDescription key={product.id} product={product} transportationMethod={transportationMethod} allProducts={products} organization={organization} />
                                    </Typography>
                                    {_.some(errors, e => e.startsWith(`product:${i}`)) && (
                                        <Alert sx={{ marginTop: '3px' }} severity='error'>Missing data</Alert>
                                    )}
                                </Box>
                                <IconButton color='error' onClick={(e) => {
                                    e.stopPropagation();
                                    setProducts(products.filter((_, idx) => idx !== i));
                                    setSelection(s => _.without(s, i).map(i2 => i2 > i ? i2 - 1 : i2));
                                    setErrors([]);
                                }} disabled={products.length === 1}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </IconButton>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {selection.includes(i) && renderProduct(product)}
                        </AccordionDetails>
                    </Accordion>
                ));
            })()}
            {/* Non-sea, non-air add product button */}
            {!isSea && !isAir && (
                <Button variant='text' color='primary' fullWidth
                    sx={{ marginTop: '12px', padding: '12px 0px', width: 'auto' }}
                    onClick={() => {
                        setSelection(s => ([...s, products.length]));
                        setProducts([...products, createEmptyProduct({ transportationMethod })]);
                    }}>+ Product</Button>
            )}
        </Box>
    )
}

export default OpportunityProductsPane;
